import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const beliefs = [
  {
    icon: 'ri-compass-3-line',
    title: '长期主义',
    desc: '我们不追求短期项目收入，而是追求与客户建立长期合作关系。只有当客户真正完成能力迁移，我们的价值才算实现。',
  },
  {
    icon: 'ri-hammer-line',
    title: '工程化思维',
    desc: '我们相信一切问题都可以通过工程化的方式解决。不依赖个人英雄主义，而是建立可复制、可扩展的系统能力。',
  },
  {
    icon: 'ri-hand-heart-line',
    title: '深度参与',
    desc: '我们不坐在会议室里给建议，而是坐在工程师旁边一起写代码。真正的理解来自一线，真正的改变来自行动。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '真实交付',
    desc: '我们只做真实项目，只交付真实结果。没有 Demo，没有精心准备的演示，所有的成果都经得起检验。',
  },
];

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/about-hero.jpg"
            alt="about hero"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/60 to-[#0F172A]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-widest text-white/40 mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
              About Us
            </span>
            <h1
              className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
            >
              关于我们
            </h1>
            <p
              className="text-lg text-white/50 leading-relaxed"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              我们是一群相信 AI 将重构软件生产方式的工程师。
            </p>
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-widest text-[#64748B] mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
                Why Kuansi
              </span>
              <h2
                className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
              >
                为什么选择宽思智能
              </h2>
            </div>
          </FadeIn>

          <FadeIn delay={200}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: '来自一线',
                  desc: '我们的团队成员都来自一线研发团队，理解真实的工程痛点，不会给出脱离实际的方案。',
                  stat: '一线背景',
                  statLabel: '5+ 年研发管理 / B 端业务深耕',
                },
                {
                  title: '真实项目',
                  desc: '我们只在客户的真实生产项目上工作，所有的方法论都来自一线实践，不另起炉灶做演示项目。',
                  stat: '真实项目',
                  statLabel: '不做演示样例',
                },
                {
                  title: '深度参与',
                  desc: '我们不是只给建议的顾问，而是参与者——我们坐在你的工程师旁边，一起写代码、一起解决问题。',
                  stat: '驻场 + 远程',
                  statLabel: '与客户团队并肩工作',
                },
              ].map((item, idx) => (
                <div key={idx} className="bg-[#F8FAFC] rounded-2xl p-8 border border-slate-100 text-center">
                  <div
                    className="text-xl md:text-2xl font-bold mb-2"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif', color: '#F97316' }}
                  >
                    {item.stat}
                  </div>
                  <p
                    className="text-xs text-[#64748B] mb-6"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {item.statLabel}
                  </p>
                  <h3
                    className="text-base font-bold text-[#0F172A] mb-3"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-sm text-[#64748B] leading-relaxed"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Cooperation */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-widest text-[#64748B] mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
                Cooperation
              </span>
              <h2
                className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
              >
                多种合作方式
              </h2>
              <p
                className="text-base text-[#64748B] max-w-2xl mx-auto"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
              >
                我们不是外包公司，也不是培训机构。我们是深度参与的合作伙伴，和你一起完成能力迁移。
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={200}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  num: '01',
                  title: '项目制',
                  desc: '以项目为单位合作，明确交付目标和时间节点。适合有明确需求的软件开发或系统改造项目。',
                  tags: ['明确交付', '固定周期', '结果导向'],
                },
                {
                  num: '02',
                  title: '驻场制',
                  desc: '团队成员驻场参与，深度融入客户研发团队。适合需要长期技术支持和能力建设的企业。',
                  tags: ['深度参与', '长期陪伴', '能力转移'],
                },
                {
                  num: '03',
                  title: '阶段性陪跑',
                  desc: '按阶段推进，每个阶段有明确目标和验收标准。适合希望逐步推进 AI 转型的企业。',
                  tags: ['分阶段推进', '灵活调整', '持续优化'],
                },
              ].map((c, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-8 border border-slate-100 h-full">
                  <div
                    className="text-3xl font-bold mb-4"
                    style={{ fontFamily: 'Inter, sans-serif', color: '#2563EB', opacity: 0.3 }}
                  >
                    {c.num}
                  </div>
                  <h3
                    className="text-lg font-bold text-[#0F172A] mb-2"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {c.title}
                  </h3>
                  <p
                    className="text-sm text-[#64748B] leading-relaxed mb-4"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {c.desc}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {c.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2.5 py-1 rounded-full bg-[#F8FAFC] border border-slate-100 text-[#64748B]"
                        style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Beliefs */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-widest text-white/40 mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
                What We Believe
              </span>
              <h2
                className="text-3xl md:text-4xl font-bold text-white"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
              >
                我们的信念
              </h2>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {beliefs.map((b, idx) => (
              <FadeIn key={idx} delay={idx * 100}>
                <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#F97316]/20 mb-6">
                    <i className={`${b.icon} text-[#F97316] text-xl`} />
                  </div>
                  <h3
                    className="text-lg font-bold text-white mb-3"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {b.title}
                  </h3>
                  <p
                    className="text-sm text-white/50 leading-relaxed"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {b.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <FadeIn>
            <h2
              className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              想更深入了解我们？
            </h2>
            <p
              className="text-base text-[#64748B] mb-8"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              预约一次交流，我们愿意分享更多真实项目中的经验和思考。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/#contact-section"
                className="px-7 py-3.5 rounded-full bg-[#F97316] text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#ea6c0a] transition-colors"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
              >
                预约交流
              </Link>
              <Link
                to="/#premise"
                className="px-7 py-3.5 rounded-full border border-slate-200 text-[#0F172A] text-sm font-medium whitespace-nowrap cursor-pointer hover:border-slate-300 transition-colors"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
              >
                了解方法论
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
